import { FC, useMemo } from 'react';

import { BugoBrand } from '@shared/api/bugoBrand/bugoBrand.interface';
import {
  AccountDateType,
  NeedAccountMonthItem,
} from '@shared/api/needAccount/needAccount.interface';
import { useAuth } from '@shared/state/hooks/useAuth';
import { getRebateDate } from '@shared/utils/dateUtil';
import { formatDate } from '@shared/utils/formatDate';
import { Radio, Table, TableColumnsType } from 'antd';
import _ from 'lodash';
import { useLocation } from 'react-router-dom';

import { AgencyAdminAccountDetailModalButton } from '../AgencyAdminAccountDetail/AgencyAdminAccountDetailModalButton';

//* 이번달 및 다음달 정산 받아야하는 내용 표시

interface Props {
  needAccountMonthly: NeedAccountMonthItem[];
  dateType?: AccountDateType;
  onDateTypeChange: (dateType: AccountDateType) => void;
}

type AccountData = {
  id: string;
  year: number;
  month: number;
  rebateDate: Date;
  rebateCount: number;
  rebateAgencyPrice: number;
  rebateWorkerPrice: number;
  feventCount: number;
  orderedFeventCount: number;
};

const columns: (bugoBrand?: BugoBrand) => TableColumnsType<AccountData> = (
  bugoBrand?: BugoBrand,
) => {
  if (bugoBrand?.bugoAgency?.shareRebateWithWorker) {
    return [
      {
        title: '정산기간',
        dataIndex: 'month',
        key: 'month',
        align: 'center',
        render: (_month: number, record: AccountData) => {
          const now = new Date();
          return (
            <p
              className={`px-6 py-1 text-base font-medium ${
                now.getTime() > getRebateDate(record.month, record.year).getTime()
                  ? 'theme-text-warn-03'
                  : ''
              }`}
            >
              {record.year}년 {record.month}월
            </p>
          );
        },
      },
      {
        title: '정산지급일',
        dataIndex: 'rebateDate',
        key: 'rebateDate',
        align: 'center',
        render: (rebateDate: Date) => {
          const now = new Date();
          if (rebateDate.getTime() < now.getTime()) {
            return (
              <p className="break-keep px-6 py-1 text-base font-medium theme-text-warn-03">
                {formatDate(rebateDate)} (미지급)
              </p>
            );
          } else {
            return (
              <p className="px-6 py-1 text-base font-medium">{formatDate(rebateDate)}</p>
            );
          }
        },
      },
      {
        title: '부고건수',
        dataIndex: 'feventCount',
        key: 'feventCount',
        align: 'center',
        render: (_: number, record: AccountData) => {
          const now = new Date();
          return (
            <p
              className={`px-6 py-1 text-base font-medium ${
                now.getTime() > getRebateDate(record.month, record.year).getTime()
                  ? 'theme-text-warn-03'
                  : ''
              }`}
            >
              {record.orderedFeventCount}/{record.feventCount}
            </p>
          );
        },
      },
      {
        title: '총 판매건수',
        dataIndex: 'rebateCount',
        key: 'rebateCount',
        align: 'center',
        render: (rebateCount: number, record: AccountData) => {
          const now = new Date();
          return (
            <p
              className={`px-6 py-1 text-base font-medium ${
                now.getTime() > getRebateDate(record.month, record.year).getTime()
                  ? 'theme-text-warn-03'
                  : ''
              }`}
            >
              {rebateCount.toLocaleString()}개
            </p>
          );
        },
      },
      {
        title: bugoBrand.bugoAgency.nickName + ' 정산금',
        dataIndex: 'rebateAgencyPrice',
        key: 'rebateAgencyPrice',
        align: 'center',
        render: (rebateAgencyPrice: number, record: AccountData) => {
          const now = new Date();
          return (
            <p
              className={`px-6 py-1 text-base font-medium ${
                now.getTime() > getRebateDate(record.month, record.year).getTime()
                  ? 'theme-text-warn-03'
                  : ''
              }`}
            >
              {rebateAgencyPrice.toLocaleString()}원
            </p>
          );
        },
      },
      {
        title: '지도사 정산금',
        dataIndex: 'rebateWorkerPrice',
        key: 'rebateWorkerPrice',
        align: 'center',
        render: (rebateWorkerPrice: number, record: AccountData) => {
          const now = new Date();
          return (
            <p
              className={`px-6 py-1 text-base font-medium ${
                now.getTime() > getRebateDate(record.month, record.year).getTime()
                  ? 'theme-text-warn-03'
                  : ''
              }`}
            >
              {rebateWorkerPrice.toLocaleString()}원
            </p>
          );
        },
      },
      {
        title: '합계',
        dataIndex: 'id',
        key: 'total',
        align: 'center',
        render: (_, record: AccountData) => {
          const now = new Date();
          return (
            <p
              className={`px-6 py-1 text-base font-medium ${
                now.getTime() > getRebateDate(record.month, record.year).getTime()
                  ? 'theme-text-warn-03'
                  : ''
              }`}
            >
              {(record.rebateWorkerPrice + record.rebateAgencyPrice).toLocaleString()}원
            </p>
          );
        },
      },
      {
        title: '세부내역보기',
        dataIndex: 'id',
        key: 'detail',
        align: 'center',
        render: (_, record: AccountData) => {
          return (
            <div className="center-box">
              <AgencyAdminAccountDetailModalButton
                year={record.year}
                month={record.month}
              />
            </div>
          );
        },
      },
    ];
  } else {
    return [
      {
        title: '정산기간',
        dataIndex: 'month',
        key: 'month',
        align: 'center',
        render: (_month: number, record: AccountData) => {
          const now = new Date();
          return (
            <p
              className={`px-6 py-1 text-base font-medium ${
                now.getTime() > getRebateDate(record.month, record.year).getTime()
                  ? 'theme-text-warn-03'
                  : ''
              }`}
            >
              {record.year}년 {record.month}월
            </p>
          );
        },
      },
      {
        title: '정산지급일',
        dataIndex: 'rebateDate',
        key: 'rebateDate',
        align: 'center',
        render: (rebateDate: Date) => {
          const now = new Date();
          if (rebateDate.getTime() < now.getTime()) {
            return (
              <p className="break-keep px-6 py-1 text-base font-medium theme-text-warn-03">
                {formatDate(rebateDate)} (미지급)
              </p>
            );
          } else {
            return (
              <p className="px-6 py-1 text-base font-medium">{formatDate(rebateDate)}</p>
            );
          }
        },
      },
      {
        title: '부고건수',
        dataIndex: 'feventCount',
        key: 'feventCount',
        align: 'center',
        render: (_: number, record: AccountData) => {
          const now = new Date();
          return (
            <p
              className={`px-6 py-1 text-base font-medium ${
                now.getTime() > getRebateDate(record.month, record.year).getTime()
                  ? 'theme-text-warn-03'
                  : ''
              }`}
            >
              {record.orderedFeventCount}/{record.feventCount}
            </p>
          );
        },
      },
      {
        title: '총 판매건수',
        dataIndex: 'rebateCount',
        key: 'rebateCount',
        align: 'center',
        render: (rebateCount: number, record: AccountData) => {
          const now = new Date();
          return (
            <p
              className={`px-6 py-1 text-base font-medium ${
                now.getTime() > getRebateDate(record.month, record.year).getTime()
                  ? 'theme-text-warn-03'
                  : ''
              }`}
            >
              {rebateCount.toLocaleString()}개
            </p>
          );
        },
      },
      {
        title: bugoBrand ? bugoBrand.bugoAgency?.nickName + ' 정산금' : '상조 정산금',
        dataIndex: 'rebateAgencyPrice',
        key: 'rebateAgencyPrice',
        align: 'center',
        render: (rebateAgencyPrice: number, record: AccountData) => {
          const now = new Date();
          return (
            <p
              className={`px-6 py-1 text-base font-medium ${
                now.getTime() > getRebateDate(record.month, record.year).getTime()
                  ? 'theme-text-warn-03'
                  : ''
              }`}
            >
              {rebateAgencyPrice.toLocaleString()}원
            </p>
          );
        },
      },
      {
        title: '세부내역보기',
        dataIndex: 'id',
        key: 'detail',
        align: 'center',
        render: (_, record: AccountData) => {
          return (
            <div className="center-box">
              <AgencyAdminAccountDetailModalButton
                year={record.year}
                month={record.month}
              />
            </div>
          );
        },
      },
    ];
  }
};

export const NeedAccountTable: FC<Props> = function NeedAccountTable({
  needAccountMonthly,
  dateType,
  onDateTypeChange,
}: Props) {
  const { userProfile } = useAuth();
  const location = useLocation();
  const showSettings = new URLSearchParams(location.search).get('settings') === 'true';

  const bugoBrand = userProfile?.bugoAgencyAdminDetail?.bugoBrands[0];

  const tableData: AccountData[] = useMemo(() => {
    // 정산기간 빠른순 (연도 오름차순 → 월 오름차순)
    const sorted = _.sortBy(needAccountMonthly, ['year', 'month']);
    return _.map(sorted, (item) => {
      return {
        id: `${item.year}-${item.month}`,
        year: item.year,
        month: item.month,
        rebateDate: getRebateDate(item.month, item.year),
        rebateCount: item.totalCount,
        rebateAgencyPrice: item.totalAgencyIncome,
        rebateWorkerPrice: item.totalWorkerIncome,
        feventCount: item.feventCount,
        orderedFeventCount: item.orderedFeventCount,
      };
    });
  }, [needAccountMonthly]);

  const presentMonthAccount = useMemo(() => {
    return (
      <Table
        className="w-full"
        columns={columns(bugoBrand)}
        dataSource={_.flatMapDeep([tableData])}
        pagination={{
          hideOnSinglePage: true,
        }}
        rowKey={'id'}
        bordered
        size={'small'}
      />
    );
  }, [bugoBrand, tableData]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="pl-4 text-base font-medium">정산예정내역</p>
        {showSettings && (
          <Radio.Group
            value={dateType}
            onChange={(e) => onDateTypeChange(e.target.value)}
            optionType="button"
            buttonStyle="solid"
            size="small"
          >
            <Radio.Button value="purchasedAt">판매일 기준</Radio.Button>
            <Radio.Button value="coffinOutDate">발인일 기준</Radio.Button>
          </Radio.Group>
        )}
      </div>
      {presentMonthAccount}
    </div>
  );
};
